'use client';

/* eslint-disable @next/next/no-img-element */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Message, MessageSource } from '@/type/chat';
import { useTranslation } from '@/lib/i18n';
import Image from 'next/image';
import { useSnackbar } from '@/contexts/SnackbarContext';
import MessageBoxTab from '@/components/Message/MessageBoxTab';
import MessageBoxToolBar from '@/components/Message/MessageBoxToolBar';
import MessageRelated from '@/components/Message/MessageRelated';
import { SoruceTabPanel } from '@/components/Message/MessageSources';
import MarkdownContent from '@/components/markdown/MarkdownContent';
import StepPanel from './StepPanel';
import MessageRisk from '@/components/Message/MessageRisk';
import Table from '@/components/markdown/overrides/Table';
import { MarkdownToJSX } from 'markdown-to-jsx';
import EventCard from '@/components/markdown/overrides/EventCard';
import ReferenceCard from '@/components/markdown/overrides/ReferenceCard';
import AsyncMarketCard from './AsyncMarketCard';

const MessageBox = ({
  message,
  messageIndex,
  history,
  loading,
  isLast,
  rewrite,
  sendMessage,
  onSwitchMessage,
  onRefreshAnswer,
  onRegenerateAssistant,
  isShared = false,
  chatId,
  query,
}: {
  message: Message;
  messageIndex: number;
  history: Message[];
  loading: boolean;
  isLast: boolean;
  rewrite: (messageId: string) => void;
  sendMessage: (message: string) => void;
  onSwitchMessage?: (message: Message) => void;
  onRefreshAnswer?: (message: Message) => void;
  onRegenerateAssistant?: (assistantMessageId: string) => void;
  isShared?: boolean;
  chatId?: string;
  query: string;
}) => {
  const { t } = useTranslation();
  const { showSuccess } = useSnackbar();
  const contentRef = useRef<any>(null);
  const toolInfoRef = useRef<any>(null);
  const tabRef = useRef<HTMLDivElement>(null);

  const [parsedMessage, setParsedMessage] = useState(message?.content);
  const [speechMessage, setSpeechMessage] = useState(message?.content);

  const [activeTab, setActiveTab] = useState<'pythra' | 'steps' | 'sources'>('pythra');
  // Track if user has manually selected a tab
  const [hasUserSelectedTab, setHasUserSelectedTab] = useState(false);

  // 当 loading 状态变化或内容变化时，自动切换 tab (仅在用户未手动选择时)
  useEffect(() => {
    // 仅当用户没有手动选择 tab 且内容加载完成时，自动切换到 pythra tab
    if (!hasUserSelectedTab && !loading && message.content && activeTab === 'steps') {
      setActiveTab('pythra');
    }
  }, [loading, message?.content, activeTab, hasUserSelectedTab]);

  // Handle tab click
  const handleTabClick = (tab: 'pythra' | 'steps' | 'sources') => {
    setActiveTab(tab);
    setHasUserSelectedTab(true);
  };

  const mdOptions: MarkdownToJSX.Options = useMemo(
    () => ({
      overrides: {
        AsyncMarketCard: {
          component: (props: any) => {
            return <AsyncMarketCard recommendations={message.raw?.recommendations} {...props} />;
          },
        },
        EventCard: {
          component: (props: any) => {
            return <EventCard {...props} />;
          },
        },
        ReferenceCard: {
          component: (props: any) => {
            return <ReferenceCard sources={message.webSources as MessageSource[]} {...props} />;
          },
        },
        table: {
          component: Table,
        },
      },
    }),
    [message.raw?.recommendations, message.webSources],
  );

  useEffect(() => {
    const regex = /\[(\d+)\]/g;
    let processedMessage = message.content;

    // 只有当 message.content 存在时才处理
    if (!message.content) {
      setParsedMessage('');
      setSpeechMessage('');
      return;
    }

    setSpeechMessage(message.content.replace(regex, ''));
    setParsedMessage(transformComments(processedMessage));
  }, [message.content]);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col space-y-4 w-full">
        {/* Tab Navigation with Sticky Container */}
        <div ref={tabRef} className="w-full">
          <MessageBoxTab
            message={{ ...message }}
            showSteps={
              !(loading && !message?.content) &&
              message?.thinkStatus &&
              message?.thinkStatus.length > 0
            }
            activeTab={activeTab}
            onTabChange={handleTabClick}
          />
        </div>

        {/* Tab Content */}
        {activeTab === 'steps' && message.thinkStatus && message.thinkStatus.length > 0 && (
          <div className="flex flex-col gap-4 w-full">
            <StepPanel
              steps={message.thinkStatus as any}
              completed
              queryLanguage={message.queryLanguage}
            />
          </div>
        )}

        {activeTab === 'sources' && message.webSources && message.webSources.length > 0 && (
          <div className="w-full flex flex-row items-center gap-2">
            <SoruceTabPanel sources={message.webSources as MessageSource[]} />
          </div>
        )}

        {/* Pythra Tab Content - search result */}
        {activeTab === 'pythra' && (
          <div className="flex flex-col space-y-2 gap-2">
            {/* ✅ 被取消的消息显示取消提示 */}
            {message.cancelled && !message.content ? (
              <div className="flex items-center gap-2">
                <div className="text-base font-light text-text-tertiary">
                  <span>{t('Response generation was cancelled.')}</span>
                </div>
              </div>
            ) : loading && !message.content ? (
              message.thinkStatus && message.thinkStatus.length > 0 ? (
                // <MessageThink thinkStatus={message.thinkStatus as ThinkStatus[]} />
                <StepPanel
                  steps={message.thinkStatus as any}
                  queryLanguage={message.queryLanguage}
                />
              ) : (
                // 显示默认加载指示器
                <div className="flex items-center gap-2 py-4">
                  <div className="animate-spin-slow">
                    <Image src="/logo.svg" width={20} height={13} alt="Logo" />
                  </div>
                  <div className="text-sm text-text-tertiary">
                    <span className="font-medium">{t('Thinking...')}</span>
                  </div>
                </div>
              )
            ) : (
              <>
                <div>
                  <MarkdownContent
                    ref={contentRef}
                    query={query}
                    selectable={false}
                    options={mdOptions}
                  >
                    {parsedMessage}
                  </MarkdownContent>

                  {/* 如果是错误消息，显示重试按钮 */}
                  {/* {message.isError && message.retryMessage && <MessageResponseError />} */}

                  {/* AI 生成声明和时间戳 */}
                  {!message.isError && (message.riskStatement || message.createdAt) && (
                    <MessageRisk
                      riskStatement={message.riskStatement}
                      createdAt={message.createdAt}
                    />
                  )}
                </div>

                {message.isError || isShared ? null : (
                  <MessageBoxToolBar
                    rewrite={rewrite}
                    message={message}
                    messageIndex={messageIndex}
                    title={query}
                    speechMessage={speechMessage}
                    contentRef={contentRef}
                    toolInfoRef={message.toolInfo ? toolInfoRef : undefined}
                    history={history} // Pass history down
                    // onRefreshAnswer={onRefreshAnswer} // Pass sendMessage down
                    // onRegenerateAssistant={onRegenerateAssistant} // ✅ 新增：重新生成回答
                    loading={loading} // ✅ 传递 loading 状态
                    // showRefreshAnswer={true}
                  />
                )}
                {message.suggestions && message.suggestions.length > 0 && (
                  <MessageRelated suggestions={message.suggestions} sendMessage={sendMessage} />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBox;

function transformComments(md: string): string {
  const startedAt = getNow();
  let output = '';
  let cursor = 0;

  try {
    while (cursor < md.length) {
      const commentStart = md.indexOf('<!--', cursor);

      if (commentStart === -1) {
        output += md.slice(cursor);
        break;
      }

      output += md.slice(cursor, commentStart);

      const comment = readComment(md, commentStart);

      if (!comment) {
        output += md.slice(commentStart);
        break;
      }

      output += renderComment(comment);
      cursor = comment.end;
    }

    return output;
  } finally {
    logTransformCommentsDuration(startedAt, md.length);
  }
}

function getNow() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function logTransformCommentsDuration(startedAt: number, inputLength: number) {
  const duration = getNow() - startedAt;

  if (duration < 16) return;

  console.debug('[MessageBox] transformComments duration', {
    duration: `${duration.toFixed(2)}ms`,
    inputLength,
  });
}

interface ParsedComment {
  body: string;
  raw: string;
  trailingWhitespace: string;
  end: number;
}

function readComment(md: string, start: number): ParsedComment | null {
  const closeIndex = md.indexOf('-->', start + 4);

  if (closeIndex === -1) {
    return null;
  }

  const commentEnd = closeIndex + 3;
  let end = commentEnd;

  while (end < md.length && isWhitespace(md[end])) {
    end += 1;
  }

  return {
    body: md.slice(start + 4, closeIndex),
    raw: md.slice(start, commentEnd),
    trailingWhitespace: md.slice(commentEnd, end),
    end,
  };
}

function renderComment(comment: ParsedComment) {
  const attrs = parseCommentAttributes(comment.body);
  const sourceId = attrs.sourceId || attrs.source_id || attrs.id;
  const renderType = attrs.renderType || attrs.render_type || attrs.type;
  const subMarket = attrs.sub_market || attrs.subMarket;

  if (!sourceId || !renderType) {
    return `${comment.raw}${comment.trailingWhitespace}`;
  }

  const componentName = getCommentComponentName(renderType);

  if (!componentName) {
    return `${comment.raw}${comment.trailingWhitespace}`;
  }

  const subMarketAttr = subMarket ? ` subMarket="${escapeMarkdownAttribute(subMarket)}"` : '';

  return withTrailingWhitespace(
    `<${componentName} sourceId="${escapeMarkdownAttribute(sourceId)}"${subMarketAttr} />`,
    comment.trailingWhitespace,
  );
}

function withTrailingWhitespace(component: string, trailingWhitespace = '') {
  return `${component}${trailingWhitespace || ' '}`;
}

function isWhitespace(char: string) {
  return char === ' ' || char === '\n' || char === '\r' || char === '\t' || char === '\f';
}

function parseCommentAttributes(commentBody: string) {
  const attrs: Record<string, string> = {};
  const attrRegex = /([\w:-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/g;
  let match: RegExpExecArray | null;

  while ((match = attrRegex.exec(commentBody)) !== null) {
    attrs[match[1]] = match[2] ?? match[3] ?? match[4] ?? '';
  }

  return attrs;
}

function getCommentComponentName(renderType: string) {
  const normalizedRenderType = renderType.trim().toLowerCase().replace(/-/g, '_');
  const componentAliases: Record<string, string> = {
    market: 'AsyncMarketCard',
    market_card: 'AsyncMarketCard',
    event: 'EventCard',
    event_card: 'EventCard',
    source: 'ReferenceCard',
    reference: 'ReferenceCard',
    reference_card: 'ReferenceCard',
  };
  const reservedTypes = new Set(['chart']);

  if (reservedTypes.has(normalizedRenderType)) {
    return null;
  }

  if (componentAliases[normalizedRenderType]) {
    return componentAliases[normalizedRenderType];
  }

  if (!/^[a-z][a-z0-9_]*$/.test(normalizedRenderType)) {
    return null;
  }

  return normalizedRenderType
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join('');
}

function isSourceRenderType(renderType?: string) {
  if (!renderType) return false;

  const normalizedRenderType = renderType.trim().toLowerCase().replace(/-/g, '_');
  return ['source', 'reference', 'reference_card'].includes(normalizedRenderType);
}

function escapeMarkdownAttribute(value: string) {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}
